import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronRight } from "lucide-react";
import { InvestmentStrategyProvider, useInvestmentStrategy, StrategyType } from "./investment-strategy/InvestmentStrategyContext";
import { strategies } from "./investment-strategy/strategyData";
import StrategyContent from "./investment-strategy/StrategyContent";
import CustomAllocationContent from "./investment-strategy/CustomAllocationContent";

interface InvestmentStrategyStepProps {
  onContinue: () => void;
  onStrategySelect: (strategy: StrategyType) => void;
}

const InvestmentStrategyContent = ({ onContinue, onStrategySelect }: InvestmentStrategyStepProps) => {
  const { 
    selectedStrategy, 
    setSelectedStrategy, 
    isStrategyConfirmed, 
    setIsStrategyConfirmed 
  } = useInvestmentStrategy();
  const [totalAllocation, setTotalAllocation] = useState(100);

  useEffect(() => {
    if (!selectedStrategy) {
      setSelectedStrategy("diversification");
    }
  }, [selectedStrategy, setSelectedStrategy]);

  const handleStrategySelect = () => {
    if (selectedStrategy) {
      setIsStrategyConfirmed(true);
    }
  };

  const handleContinue = () => {
    if (selectedStrategy && totalAllocation === 100) {
      onStrategySelect(selectedStrategy);
      onContinue();
    }
  };

  const handleTabChange = (value: string) => {
    setSelectedStrategy(value as StrategyType);
    setIsStrategyConfirmed(false);
  };

  const handleTotalChange = (total: number) => {
    setTotalAllocation(total);
  };

  return (
    <div className="flex flex-col">
      <Tabs 
        defaultValue="diversification" 
        className="w-full"
        onValueChange={handleTabChange}
      >
        {/* Strategy Buttons Container */}
        <div className="w-full p-1.5 mb-6">
          <TabsList className="w-full grid grid-cols-2 md:grid-cols-5 gap-1.5 [&>*:last-child]:col-span-2 md:[&>*:last-child]:col-span-1">
            {strategies.map((strategy) => (
              <TabsTrigger 
                key={strategy.id} 
                value={strategy.id}
                className="w-full px-3 py-2.5 text-sm font-medium shadow-[0_1px_2px_rgba(0,0,0,0.08)] data-[state=active]:shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
              >
                {strategy.label}
              </TabsTrigger>
            ))}
            <TabsTrigger 
              value="custom"
              className="w-full px-3 py-2.5 text-sm font-medium inline-flex items-center justify-center shadow-[0_1px_2px_rgba(0,0,0,0.08)] data-[state=active]:shadow-[0_1px_3px_rgba(0,0,0,0.12)]"
            >
              + Advanced
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Content Container */}
        <div className="mt-6 space-y-6">
          {strategies.map((strategy) => (
            <TabsContent key={strategy.id} value={strategy.id}>
              <StrategyContent strategy={strategy} onTotalChange={handleTotalChange} />
            </TabsContent>
          ))}
          
          <TabsContent value="custom">
            <CustomAllocationContent onTotalChange={handleTotalChange} />
          </TabsContent>
        </div>
      </Tabs>

      <div className="flex justify-between items-center mt-8 pt-4 border-t">
        <Button 
          variant={isStrategyConfirmed ? "default" : "outline"}
          disabled={!selectedStrategy}
          onClick={handleStrategySelect}
          className="text-sm"
        >
          Select
        </Button>
        <Button 
          onClick={handleContinue} 
          disabled={!isStrategyConfirmed || totalAllocation !== 100}
          title={totalAllocation !== 100 ? "Total allocation must equal 100%" : undefined}
        >
          Continue
          <ChevronRight className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

const InvestmentStrategyStep = (props: InvestmentStrategyStepProps) => {
  return (
    <InvestmentStrategyProvider>
      <InvestmentStrategyContent {...props} />
    </InvestmentStrategyProvider>
  );
};

export default InvestmentStrategyStep;